import type { Track, Waypoint } from '@/types/tour'

/**
 * Sum a BRouter per-segment stat across all segments of a track.
 * Returns null if no segment reports a value (e.g. GPX-imported track).
 */
function sumSegmentStat(
  track: Track,
  field: 'filteredAscend' | 'filteredDescend' | 'trackLength' | 'totalTime',
): number | null {
  if (!track.segments || track.segments.length === 0) return null
  let total = 0
  let any = false
  for (const seg of track.segments) {
    const v = seg?.[field]
    if (typeof v === 'number' && Number.isFinite(v)) {
      total += v
      any = true
    }
  }
  return any ? total : null
}

function getTrackPointElevations(track: Track): number[] {
  const elevations: number[] = []
  for (const segment of track.segments || []) {
    for (const point of segment.points || []) {
      if (typeof point.ele === 'number' && Number.isFinite(point.ele)) {
        elevations.push(point.ele)
      }
    }
  }
  return elevations
}

function getTrackPointTimeBounds(track: Track): {
  firstTime: number | null
  lastTime: number | null
} {
  let firstTime: number | null = null
  let lastTime: number | null = null

  for (const segment of track.segments || []) {
    for (const point of segment.points || []) {
      if (typeof point.time !== 'number' || !Number.isFinite(point.time)) continue
      if (firstTime === null || point.time < firstTime) {
        firstTime = point.time
      }
      if (lastTime === null || point.time > lastTime) {
        lastTime = point.time
      }
    }
  }

  return { firstTime, lastTime }
}

/**
 * Return elevation gain for a track, preferring track.metrics (GPX/FIT imports)
 * and falling back to summing BRouter `filteredAscend` across segments.
 */
export function getTrackElevationGain(track: Track): number | null {
  const fromMetrics = track.metrics?.elevationGain
  if (fromMetrics !== null && fromMetrics !== undefined) return fromMetrics

  const fromSegments = sumSegmentStat(track, 'filteredAscend')
  if (fromSegments !== null) return fromSegments

  return calculateTrackElevation(track).elevationGain
}

/**
 * Return elevation loss for a track, preferring track.metrics and falling back
 * to summing BRouter `filteredDescend` across segments.
 */
export function getTrackElevationLoss(track: Track): number | null {
  const fromMetrics = track.metrics?.elevationLoss
  if (fromMetrics !== null && fromMetrics !== undefined) return fromMetrics

  const fromSegments = sumSegmentStat(track, 'filteredDescend')
  if (fromSegments !== null) return fromSegments

  return calculateTrackElevation(track).elevationLoss
}

/**
 * Return minimum elevation for a track, preferring persisted metrics and
 * falling back to the track's waypoint elevations when available.
 */
export function getTrackElevationMin(track: Track): number | null {
  const fromMetrics = track.metrics?.elevationMin
  if (fromMetrics !== null && fromMetrics !== undefined) return fromMetrics

  const elevations = getTrackPointElevations(track)
  if (elevations.length === 0) return null
  return Math.round(elevations.reduce((min, value) => Math.min(min, value), Infinity))
}

/**
 * Return maximum elevation for a track, preferring persisted metrics and
 * falling back to the track's waypoint elevations when available.
 */
export function getTrackElevationMax(track: Track): number | null {
  const fromMetrics = track.metrics?.elevationMax
  if (fromMetrics !== null && fromMetrics !== undefined) return fromMetrics

  const elevations = getTrackPointElevations(track)
  if (elevations.length === 0) return null
  return Math.round(elevations.reduce((max, value) => Math.max(max, value), -Infinity))
}

/**
 * Return elapsed time for a track, preferring persisted metrics and falling
 * back to summed BRouter per-segment totalTime values.
 */
export function getTrackTimeElapsed(track: Track): number | null {
  const fromMetrics = track.metrics?.timeElapsed
  if (fromMetrics !== null && fromMetrics !== undefined) return fromMetrics

  const fromSegments = sumSegmentStat(track, 'totalTime')
  if (fromSegments !== null) return fromSegments

  const { firstTime, lastTime } = getTrackPointTimeBounds(track)
  if (firstTime === null || lastTime === null || lastTime <= firstTime) return null
  return Math.round((lastTime - firstTime) / 1000)
}

/**
 * Return moving time for a track, preferring persisted metrics and falling
 * back to summed BRouter per-segment totalTime values.
 */
export function getTrackTimeMoving(track: Track): number | null {
  const fromMetrics = track.metrics?.timeMoving
  if (fromMetrics !== null && fromMetrics !== undefined) return fromMetrics

  const fromSegments = sumSegmentStat(track, 'totalTime')
  if (fromSegments !== null) return fromSegments

  return calculateMovingTime(track)
}

/**
 * Calculate Haversine distance between two points
 * @param lat1 Latitude of first point
 * @param lon1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lon2 Longitude of second point
 * @returns Distance in meters
 */
export function calculateHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371e3 // Earth radius in meters
  const φ1 = (lat1 * Math.PI) / 180
  const φ2 = (lat2 * Math.PI) / 180
  const Δφ = ((lat2 - lat1) * Math.PI) / 180
  const Δλ = ((lon2 - lon1) * Math.PI) / 180

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * Calculate total distance for a track
 * @param track Track with segments and points
 * @returns Total distance in meters
 */
export function calculateTrackDistance(track: Track): number {
  let totalDistance = 0

  if (typeof track.metrics?.distance === 'number' && Number.isFinite(track.metrics.distance)) {
    // Handle API tracks with reduced coordinate data
    totalDistance = track.metrics.distance
  } else {
    // Prefer BRouter per-segment trackLength when available (drawing mode).
    const brouterLength = sumSegmentStat(track, 'trackLength')
    if (brouterLength !== null && brouterLength > 0) {
      return brouterLength
    }
    // Fall back to haversine over segment points
    track.segments.forEach((segment) => {
      const points = segment.points
      for (let i = 1; i < points.length; i++) {
        const p1 = points[i - 1]
        const p2 = points[i]
        totalDistance += calculateHaversineDistance(p1.lat, p1.lon, p2.lat, p2.lon)
      }
    })
  }

  return totalDistance
}

/**
 * Apply moving average smoothing to elevation data
 * @param elevations Array of elevation values
 * @param windowSize Size of the moving window
 * @returns Smoothed elevation array
 */
function smoothElevationData(elevations: number[], windowSize: number = 5): number[] {
  if (elevations.length <= windowSize) return elevations

  const smoothed: number[] = []
  const halfWindow = Math.floor(windowSize / 2)

  // Process in chunks for better performance with large arrays
  const CHUNK_SIZE = 10000

  for (let i = 0; i < elevations.length; i++) {
    let sum = 0
    let count = 0

    for (
      let j = Math.max(0, i - halfWindow);
      j <= Math.min(elevations.length - 1, i + halfWindow);
      j++
    ) {
      sum += elevations[j]
      count++
    }

    smoothed.push(sum / count)

    // Allow event loop to process other tasks for very large files
    if (i > 0 && i % CHUNK_SIZE === 0) {
      // This is a synchronous function, so we can't use async/await
      // But the chunking still helps prevent stack overflow
    }
  }

  return smoothed
}

/**
 * Calculate elevation statistics for a single track
 * @param track A single track with elevation data
 * @returns Elevation and distance statistics
 */
export function calculateTrackElevation(track: Track) {
  let min: number | null = null
  let max: number | null = null
  let gain: number | null = null
  let loss: number | null = null
  let distance: number = 0
  let timeMoving: number | null = null
  let timeElapsed: number | null = null

  let firstTime: number | null = null
  let lastTime: number | null = null
  const segmentDistance = sumSegmentStat(track, 'trackLength')
  const segmentTime = sumSegmentStat(track, 'totalTime')
  const segmentGain = sumSegmentStat(track, 'filteredAscend')
  const segmentLoss = sumSegmentStat(track, 'filteredDescend')

  // Collect all elevation data for smoothing
  const allElevations: number[] = []
  const allPoints: Waypoint[] = []

  // Process each segment in the track for elevation data
  track.segments.forEach((segment) => {
    segment.points.forEach((point: Waypoint) => {
      if (typeof point.ele === 'number') {
        allElevations.push(point.ele)
        allPoints.push(point)
      }
    })
  })

  // Calculate distance and time for all points regardless of elevation data
  track.segments.forEach((segment) => {
    let prevPoint: Waypoint | undefined

    segment.points.forEach((point: Waypoint) => {
      // Distance calculations
      if (prevPoint) {
        distance += calculateHaversineDistance(prevPoint.lat, prevPoint.lon, point.lat, point.lon)
      }

      // Time calculations
      if (typeof point.time === 'number') {
        if (firstTime === null) {
          firstTime = point.time
        }
        lastTime = point.time
      }

      prevPoint = point
    })
  })

  // Calculate elapsed time if we have time data
  if (firstTime !== null && lastTime !== null && lastTime > firstTime) {
    timeElapsed = Math.round((lastTime - firstTime) / 1000) // Convert to seconds

    // Calculate moving time by detecting stationary periods
    timeMoving = calculateMovingTime(track)
  }

  if (segmentTime !== null) {
    timeMoving = Math.round(segmentTime)
    timeElapsed = Math.round(segmentTime)
  }

  if (allElevations.length === 0) {
    // No elevation data available, but return calculated distance and time
    return {
      elevationMin: null,
      elevationMax: null,
      elevationGain: segmentGain !== null ? Math.round(segmentGain) : null,
      elevationLoss: segmentLoss !== null ? Math.round(segmentLoss) : null,
      distance:
        segmentDistance !== null
          ? Math.round(segmentDistance)
          : distance > 0
            ? Math.round(distance)
            : null,
      timeMoving: timeMoving,
      timeElapsed: timeElapsed,
    }
  }

  // Apply smoothing to reduce GPS noise
  const smoothedElevations = smoothElevationData(allElevations)

  // Calculate min/max from raw data safely (avoid stack overflow with large arrays)
  min = allElevations.reduce((a, b) => Math.min(a, b), Infinity)
  max = allElevations.reduce((a, b) => Math.max(a, b), -Infinity)

  // Calculate gain/loss using smoothed data
  let currentGain = 0
  let currentLoss = 0
  let totalGain = 0
  let totalLoss = 0
  const ELEVATION_THRESHOLD = 1.0

  for (let i = 1; i < smoothedElevations.length; i++) {
    const diff = smoothedElevations[i] - smoothedElevations[i - 1]

    if (diff > 0) {
      // Going uphill
      currentGain += diff
      if (currentLoss > ELEVATION_THRESHOLD) {
        totalLoss += currentLoss
        currentLoss = 0
      }
    } else if (diff < 0) {
      // Going downhill
      currentLoss += Math.abs(diff)
      if (currentGain > ELEVATION_THRESHOLD) {
        totalGain += currentGain
        currentGain = 0
      }
    }
  }

  // Add remaining gain/loss if above threshold
  if (currentGain > ELEVATION_THRESHOLD) {
    totalGain += currentGain
  }
  if (currentLoss > ELEVATION_THRESHOLD) {
    totalLoss += currentLoss
  }

  gain = segmentGain !== null ? Math.round(segmentGain) : totalGain > 0 ? totalGain : null
  loss = segmentLoss !== null ? Math.round(segmentLoss) : totalLoss > 0 ? totalLoss : null

  return {
    elevationMin: min !== null ? Math.round(min) : null,
    elevationMax: max !== null ? Math.round(max) : null,
    elevationGain: gain !== null ? Math.round(gain) : null,
    elevationLoss: loss !== null ? Math.round(loss) : null,
    distance:
      segmentDistance !== null
        ? Math.round(segmentDistance)
        : distance > 0
          ? Math.round(distance)
          : null,
    timeMoving: timeMoving,
    timeElapsed: timeElapsed,
  }
}

/**
 * Calculate moving time for a single track by detecting stationary periods
 * @param track A single track with time and position data
 * @returns Moving time in seconds, or null if no time data available
 */
export function calculateMovingTime(track: Track): number | null {
  let movingTime = 0
  const STATIONARY_THRESHOLD_MPS = 0.5 // 0.5 m/s (1.8 km/h) threshold for considering movement
  const MIN_TIME_INTERVAL = 1 // Minimum 1 second between points to consider

  track.segments.forEach((segment) => {
    const points = segment.points

    for (let i = 1; i < points.length; i++) {
      const prevPoint = points[i - 1]
      const currPoint = points[i]

      // Skip if either point doesn't have time data
      if (typeof prevPoint.time !== 'number' || typeof currPoint.time !== 'number') {
        continue
      }

      const timeDiff = (currPoint.time - prevPoint.time) / 1000 // Convert to seconds

      // Skip very short time intervals to avoid noise
      if (timeDiff < MIN_TIME_INTERVAL) {
        continue
      }

      // Calculate distance between points
      const distance = calculateHaversineDistance(
        prevPoint.lat,
        prevPoint.lon,
        currPoint.lat,
        currPoint.lon,
      )

      // Calculate speed (m/s)
      const speed = distance / timeDiff

      // If speed is above threshold, count this as moving time
      if (speed > STATIONARY_THRESHOLD_MPS) {
        movingTime += timeDiff
      }
    }
  })

  return movingTime > 0 ? Math.round(movingTime) : null
}
